import { ChainModel } from './ChainModel';

export interface CalibrationParameters {
  id: string;
  baseline_id: string;
  baseline_obj: CalibrationParameters;
  letter_number: number;
  gates: Gates;
  gps_speed?: boolean;
  combine_power?: boolean;
  alt_spd?: number;
  alt_cad?: number;
  alt_power?: number;
  autocal_params?: Record<string, boolean>;
  parameters_inherited?: any;
  parameters_manual?: any;
  // Server-derived: params holding a seeded default, not a real calibration.
  parameters_defaulted?: string[];
  elevation_difference?: number;
  elevation_manual?: boolean;
  combined_mass?: number;
  crr?: number;
  power_meter_slope?: number;
  cp_corr?: number;
  yaw_offset?: number;
  wheel_circumference?: number;
  cda_ref?: number;
  chest_ref?: number;
  head_ref?: number;
  chest_slope?: number;
  head_slope?: number;
  airspeed_ref?: number;
  airspeed_slope?: number;
  xw_wheel_compensation?: number;
  xw_para_slope?: number;
  xw_stall_slope?: number;
  xw_stall_angle?: number;
  speed_sensor_pos?: number;
  wheel_magnet_count?: number;
  magnet_details?: MagnetDetails | null;
  ground_speed_setup?: GroundSpeedSetup | null;
  drivetrain?: { front_tooth_count?: number } | null;
  model_chain_id?: string | null;
  chain_model?: ChainModel | null;
}

// One calibrated wheel's magnet angular spacing (cumulative degrees,
// angles_deg[0] === 0, anchored at the first magnet after the largest gap).
export interface MagnetDetailsEntry {
  ant_device_number?: number;
  magnet_count: number;
  angles_deg: number[];
  gap_sigma_deg?: number[];
  quality?: number;
  r_squared?: number;
  n_windows?: number;
  status?: string;
  n_slips_healed?: number;
  // On RUN objects only: pulses whose timestamps the wheel-speed calc
  // re-placed after the fit (jerk-limit restamping).
  n_jerk_restamped?: number;
  source_run_id?: string;
  calibrated_at?: string;
}

// Keys are speed-sensor ANT device numbers; JSONB serialises them as strings.
export type MagnetDetails = Record<string, MagnetDetailsEntry>;

// PositionEnum ordinals (cscore.api_types.ground_speed_type) — gaps left
// for growth; *_ROLLER are the off-bike (trainer) variants.
export const SPEED_POSITION = {
  UNKNOWN: 0,
  FRONT: 10,
  REAR: 20,
  FRONT_ROLLER: 30,
  REAR_ROLLER: 40,
} as const;

// One speed sensor's place in the unified setup. id_ is the ANT device
// number (or BLE pseudo device number). run_instance is device-populated
// per run and never set on configs; session_instance is a future
// config-revision counter (0 today).
export interface SpeedSensorEntry {
  id_: number;
  position?: number;
  run_instance?: number | null;
  session_instance?: number;
  magnet_count?: number;
  magnet_spacing?: MagnetDetailsEntry | null;
}

// The unified speed-sensor setup on config (calibrated intent) and run
// (effective per-run record). Supersedes gps_speed / alt_spd /
// speed_sensor_pos / wheel_magnet_count / magnet_details, which remain
// readable for legacy rows.
export interface GroundSpeedSetup {
  gps_speed?: boolean;
  primary_id?: number | null;
  // Which wheel the speed source is: SPEED_POSITION.FRONT | REAR only.
  // The session-calibration claim that exists BEFORE any sensor ids do;
  // null/absent = never chosen (effective side defaults to front).
  source_side?: number | null;
  sensors?: SpeedSensorEntry[];
}

export interface Gates {
  id: string;
  elevation_manual: boolean;
  elevation_automatic: boolean;
  address?: string | null;
  notes?: string | null;
  description?: string | null;
  route_description?: string | null;
}
